import { action, makeObservable, observable } from 'mobx';
import { DashboardService } from '/src/api';
import BaseStore from '/src/context/stores/baseStore';
import RootStore from '/src/context/stores/rootStore';
import { DashboardData, TClientId } from '/src/lib/models';
import { CountryId, CountryName } from '/src/lib/types';
import { userHasClient } from '/src/utils';

const toNumber = (value: number | string): number => (value ? parseFloat(value.toString()) : 0);

const mergeCombinedAdminDashboards = (usa: DashboardData, canada: DashboardData): DashboardData => ({
  kwh_consumed: toNumber(usa.kwh_consumed) + toNumber(canada.kwh_consumed),
  mt_cO2e_displaced: toNumber(usa.mt_cO2e_displaced) + toNumber(canada.mt_cO2e_displaced),
  total_recs_required: (toNumber(usa.total_recs_required) + toNumber(canada.total_recs_required)).toFixed(2),
  num_enrolled_equipment: usa.num_enrolled_equipment + canada.num_enrolled_equipment,
  days_to_next_credit_minting: usa.days_to_next_credit_minting,
  reporting_period_type_id: usa.reporting_period_type_id,
  total_net_revenue: '0',
  average_dollar_per_kwh: '0',
  percent_revenue_per_equipment_category: [],
  percent_revenue_per_program: [],
  reporting_periods: [],
  facilities: [],
  countries: [
    {
      country_id: CountryId.USA,
      currency_code: 'USD',
      total_net_revenue: usa.total_net_revenue,
      average_dollar_per_kwh: usa.average_dollar_per_kwh,
      percent_revenue_per_program: usa.percent_revenue_per_program,
      percent_revenue_per_equipment_category: usa.percent_revenue_per_equipment_category,
    },
    {
      country_id: CountryId.Canada,
      currency_code: 'CAD',
      total_net_revenue: canada.total_net_revenue,
      average_dollar_per_kwh: canada.average_dollar_per_kwh,
      percent_revenue_per_program: canada.percent_revenue_per_program,
      percent_revenue_per_equipment_category: canada.percent_revenue_per_equipment_category,
    },
  ],
});

export default class DashboardStore extends BaseStore {
  dashboardData?: DashboardData;

  defaultCountry = CountryName.USA;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      dashboardData: observable,
      defaultCountry: observable,
      setDashboardData: action,
      clearDashboardData: action,
      getAdminView: action,
      getCombinedAdminView: action,
      getClientView: action,
    });
  }

  setDashboardData = (dashboardData?: DashboardData) => {
    this.dashboardData = dashboardData;
  };

  clearDashboardData = () => {
    this.dashboardData = undefined;
  };

  getAdminView = async (country = this.defaultCountry) => {
    if (!this.rootStore.userStore.isAdminUser) return;

    const dashboardData = await DashboardService.getAdminView({ country: country.toLowerCase() as CountryName });

    this.setDashboardData({ client_id: 0, ...dashboardData });
  };

  getCombinedAdminView = async () => {
    if (!this.rootStore.userStore.isAdminUser) return;

    const [usa, canada] = await Promise.all([
      DashboardService.getAdminView({ country: CountryName.USA }),
      DashboardService.getAdminView({ country: CountryName.Canada }),
    ]);

    this.setDashboardData({ client_id: 0, ...mergeCombinedAdminDashboards(usa, canada) });
  };

  getClientView = async (clientId: TClientId) => {
    if (
      !this.rootStore.userStore.user ||
      (this.rootStore.userStore.isExternalUser && !userHasClient(this.rootStore.userStore.user, clientId))
    )
      return;

    const dashboardData = await DashboardService.getClientView({ client_id: clientId });

    this.setDashboardData({ client_id: clientId, ...dashboardData });
  };
}
